import { map } from 'lodash';
import { normalize, schema } from 'normalizr';
import { createLogic } from 'redux-logic';
import { hosts } from 'settings';
import { decorateRecipe, decorateRecipes } from 'decorators';

// Action Types
const FETCH = '@@app/recipe/FETCH';
const FETCH_COLLECTION = '@@app/recipe/FETCH_COLLECTION';
const FETCH_COLLECTIONS = '@@app/recipe/FETCH_COLLECTIONS';
const FETCH_SUCCESS = '@@app/recipe/FETCH_SUCCESS';
const FETCH_ERROR = '@@app/recipe/FETCH_ERROR';
const FETCH_INSPIRATION = '@@app/recipe/FETCH_INSPIRATION';
const FETCH_INSPIRATION_SUCCESS = '@@app/recipe/FETCH_INSPIRATION_SUCCESS';
const CANCEL = '@@app/recipe/CANCEL';

// Initial State
export const initialState = {
  isLoading: false,
  result: undefined,
  pagination: {},
  entities: {
    recipes: {},
    collections: {},
    inspiration: [],
  },
};

// Observables
const fetchRecipeLogic = createLogic({
  type: FETCH,
  cancelType: CANCEL,
  processOptions: {
    successType: FETCH_SUCCESS,
    failType: FETCH_ERROR,
  },
  process({ action, httpClient }) {
    const { recipeId } = action.payload;
    const recipesSchema = new schema.Entity('recipes');

    return httpClient
      .get(`/v1/voome/recipes/${recipeId}`)
      .then(response =>
        normalize(decorateRecipe(response.data), recipesSchema)
      );
  },
});

const fetchCollectionsLogic = createLogic({
  type: FETCH_COLLECTIONS,
  cancelType: CANCEL,
  processOptions: {
    successType: FETCH_SUCCESS,
    failType: FETCH_ERROR,
  },
  process({ action, httpClient }) {
    const { subscriptionId, page } = action.payload;
    const endpoint = `/v1/voome/recipe-collections/all-ids`;
    const params = { subscriptionId, page };
    const collection = new schema.Entity('collections');

    return httpClient.get(endpoint, { params }).then(response => {
      const { data, pagination } = response;
      const { entities, result } = normalize(data, [collection]);

      return {
        entities,
        pagination: { [page]: { ...pagination, result } },
      };
    });
  },
});

const fetchCollectionLogic = createLogic({
  type: FETCH_COLLECTION,
  cancelType: CANCEL,
  processOptions: {
    successType: FETCH_SUCCESS,
    failType: FETCH_ERROR,
  },
  process({ action, httpClient }) {
    const { key, voomeProductId } = action.payload;
    const endpoint = `/v1/voome/recipe-collections/${key}`;
    const params = { voomeProductId };

    const onSuccess = response => {
      const data = {
        ...response.data,
        recipes: decorateRecipes(response.data.recipes),
      };

      const recipe = new schema.Entity('recipes');
      const collection = new schema.Entity('collections', {
        recipes: [recipe],
      });

      return normalize(data, collection);
    };

    return httpClient.get(endpoint, { params }).then(onSuccess);
  },
});

const fetchInspirationLogic = createLogic({
  type: FETCH_INSPIRATION,
  cancelType: CANCEL,
  processOptions: {
    successType: FETCH_INSPIRATION_SUCCESS,
    failType: FETCH_ERROR,
  },
  process({ action, httpClient }) {
    const { memberId, params } = action.payload;
    const recipes = [new schema.Entity('recipes')];

    return httpClient
      .get(`/v1/members/${memberId}/voome-profile/recipe-inspo`, { params })
      .then(response => normalize(decorateRecipes(response.data), recipes));
  },
});

export const recipeLogic = [
  fetchRecipeLogic,
  fetchCollectionsLogic,
  fetchCollectionLogic,
  fetchInspirationLogic,
];

// Selectors
export const selectRecipes = state => {
  return state.entities.recipes;
};

export const selectRecipe = (state, recipeId) => {
  return selectRecipes(state)[recipeId] || {};
};

export const selectRecipesByIds = (state, ids) => {
  return map(ids, id => selectRecipe(state, id));
};

export const selectRecipeCollections = state => {
  return state.entities.collections;
};

export const selectRecipeCollection = (state, hackId) => {
  const collection = state.entities.collections[hackId];

  return collection
    ? {
        ...collection,
        recipes: selectRecipesByIds(state, collection.recipes),
      }
    : undefined;
};

export const selectRecipeCollectionsPagination = (state, page) => {
  const pagination = state.pagination && state.pagination[page];

  return pagination
    ? {
        ...pagination,
        result: pagination.result.map(id => state.entities.collections[id]),
      }
    : {};
};

export const selectRecipeInspiration = state => {
  return selectRecipesByIds(state, state.entities.inspiration);
};

// Helpers
export const recipePrintUri = (recipeId, subscriptionId) => {
  return recipeId && subscriptionId
    ? `${
        hosts.api
      }/v1/voome/recipes/${recipeId}/print?subscription_id=${subscriptionId}`
    : '#';
};

// Action Creators
export function fetchRecipe(recipeId) {
  return { type: FETCH, payload: { recipeId } };
}

export function fetchRecipeCollections(subscriptionId, page) {
  return { type: FETCH_COLLECTIONS, payload: { subscriptionId, page } };
}

export function fetchRecipeCollection(key, voomeProductId) {
  return { type: FETCH_COLLECTION, payload: { key, voomeProductId } };
}

export function fetchRecipeInspiration(memberId, level = 0) {
  const params = { level };
  return { type: FETCH_INSPIRATION, payload: { memberId, params } };
}

// Reducer
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH:
    case FETCH_COLLECTIONS:
    case FETCH_COLLECTION:
      return {
        ...state,
        isLoading: true,
      };

    case FETCH_SUCCESS:
      return {
        ...state,
        isLoading: false,
        result: payload.result,
        pagination: {
          ...state.pagination,
          ...payload.pagination,
        },
        entities: {
          ...state.entities,
          recipes: {
            ...state.entities.recipes,
            ...payload.entities.recipes,
          },
          collections: {
            ...state.entities.collections,
            ...payload.entities.collections,
          },
        },
      };

    case FETCH_INSPIRATION_SUCCESS:
      return {
        ...state,
        isLoading: false,
        entities: {
          ...state.entities,
          inspiration: payload.result,
          recipes: {
            ...state.entities.recipes,
            ...payload.entities.recipes,
          },
        },
      };

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/logic/recipe.js